import { useEffect, useState } from "react";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, GripVertical, Sparkles } from "lucide-react";

const emptyMember = () => ({
  id: `tm_new_${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  title: "",
  image_url: "",
  description: "",
  email: "",
  sort_order: 0,
  coming_soon: false,
});

const ContactPageEditor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/contact-page`);
      const text = await res.text();
      setData(text ? JSON.parse(text) : {});
    } catch {
      toast.error("Could not load contact page");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setData((d) => ({ ...(d || {}), [k]: v }));

  const updateMember = (idx, field, value) => {
    setData((d) => {
      const next = [...(d?.team_members || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...d, team_members: next };
    });
  };

  const addMember = () => setData((d) => ({
    ...d,
    team_members: [...(d?.team_members || []), { ...emptyMember(), sort_order: (d?.team_members || []).length }],
  }));

  const removeMember = (idx) => setData((d) => ({
    ...d,
    team_members: (d?.team_members || []).filter((_, i) => i !== idx),
  }));

  const moveMember = (idx, dir) => setData((d) => {
    const list = [...(d?.team_members || [])];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return d;
    [list[idx], list[j]] = [list[j], list[idx]];
    return { ...d, team_members: list.map((m, i) => ({ ...m, sort_order: i })) };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        heading: data?.heading || "",
        subheading: data?.subheading || "",
        general_email: data?.general_email || "",
        general_phone: data?.general_phone || "",
        address: data?.address || "",
        team_members: (data?.team_members || []).map((m, i) => ({ ...m, sort_order: i })),
      };
      const res = await fetch(`${API}/contact-page`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        let err; try { err = JSON.parse(text); } catch { err = { detail: text }; }
        throw new Error(err?.detail || "Save failed");
      }
      toast.success("Contact page saved");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="contact-page-editor">
      <Card className="border border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-slate-900">Contact Us Page</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Public page at <span className="font-mono">/contact</span>. Update intro copy and the team directory.</p>
          </div>
          <Button onClick={save} disabled={saving} className="btn-primary" data-testid="contact-save-btn">
            {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving</>) : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="cp-heading">Heading</Label>
              <Input id="cp-heading" value={data?.heading || ""} onChange={(e) => set("heading", e.target.value)} data-testid="cp-heading" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cp-sub">Subheading</Label>
              <Textarea id="cp-sub" rows={2} value={data?.subheading || ""} onChange={(e) => set("subheading", e.target.value)} data-testid="cp-subheading" />
            </div>
            <div>
              <Label htmlFor="cp-email">General email</Label>
              <Input id="cp-email" value={data?.general_email || ""} onChange={(e) => set("general_email", e.target.value)} data-testid="cp-email" />
            </div>
            <div>
              <Label htmlFor="cp-phone">General phone</Label>
              <Input id="cp-phone" value={data?.general_phone || ""} onChange={(e) => set("general_phone", e.target.value)} data-testid="cp-phone" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cp-addr">Address</Label>
              <Input id="cp-addr" value={data?.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="cp-address" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-slate-900">Team & Instructors</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Tip: mark unfinished profiles as "Coming soon" — they'll show a yellow ribbon and hide the email.</p>
          </div>
          <Button onClick={addMember} variant="outline" data-testid="cp-add-member"><Plus className="w-4 h-4 mr-1" /> Add member</Button>
        </CardHeader>
        <CardContent>
          {(!data?.team_members || data.team_members.length === 0) ? (
            <p className="text-sm text-slate-500">No team members yet — the public page will show a "Coming soon" placeholder.</p>
          ) : (
            <div className="space-y-3">
              {data.team_members.map((m, idx) => (
                <div key={m.id || idx} className="border border-slate-200 rounded-lg p-4" data-testid={`cp-member-${idx}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-4 items-start">
                    <div className="space-y-2">
                      {m.image_url ? (
                        <img src={m.image_url} alt={m.name} className="w-24 h-24 rounded-lg object-cover border border-slate-200" />
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-xs">No photo</div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input value={m.name || ""} onChange={(e) => updateMember(idx, "name", e.target.value)} placeholder="e.g. Tara May" data-testid={`cp-name-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Input value={m.title || ""} onChange={(e) => updateMember(idx, "title", e.target.value)} placeholder="e.g. Program Director" data-testid={`cp-title-${idx}`} />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Image URL</Label>
                          <Input value={m.image_url || ""} onChange={(e) => updateMember(idx, "image_url", e.target.value)} placeholder="https://… (paste a public photo URL)" data-testid={`cp-image-${idx}`} />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Description</Label>
                          <Textarea rows={2} value={m.description || ""} onChange={(e) => updateMember(idx, "description", e.target.value)} placeholder="A short bio (1-2 sentences)" data-testid={`cp-desc-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input value={m.email || ""} onChange={(e) => updateMember(idx, "email", e.target.value)} placeholder="person@aspiritech.org" data-testid={`cp-email-${idx}`} />
                        </div>
                        <div className="flex items-end gap-2">
                          <label className="text-sm text-slate-700 inline-flex items-center gap-2 mb-2">
                            <input type="checkbox" checked={!!m.coming_soon} onChange={(e) => updateMember(idx, "coming_soon", e.target.checked)} data-testid={`cp-coming-${idx}`} />
                            <Sparkles className="w-3.5 h-3.5 text-[#FFD507]" /> Coming soon
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 text-slate-500">
                      <button type="button" onClick={() => moveMember(idx, -1)} className="px-2 py-1 hover:text-slate-900 disabled:opacity-30" disabled={idx === 0} title="Move up" data-testid={`cp-up-${idx}`}>
                        <GripVertical className="w-4 h-4 rotate-180" />
                      </button>
                      <button type="button" onClick={() => moveMember(idx, +1)} className="px-2 py-1 hover:text-slate-900 disabled:opacity-30" disabled={idx === data.team_members.length - 1} title="Move down" data-testid={`cp-down-${idx}`}>
                        <GripVertical className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => removeMember(idx)} className="px-2 py-1 text-red-500 hover:text-red-700" title="Remove" data-testid={`cp-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactPageEditor;
